import { Edit as EditIcon, HealthAndSafety } from "@mui/icons-material";
import { Alert, Avatar, Box, Button, Card, CircularProgress, Divider, List, ListItem, ListItemAvatar, ListItemText, Tab, Tabs, Typography, useTheme } from "@mui/material";
import React, { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useBranding } from '../../../contexts/BrandingContext';
import { useAuth } from "../../../contexts/AuthContext";
import { parseApiError } from "../../../library/apiErrorHelper";
import { CatalogDraft, IServiceCatalog, invalidNodeKeys, localised, toCatalogDraft, toServiceCatalogRequest } from "../../../library/models/serviceCatalog";
import ResourcesIds from "../../../resources/ResourcesIds";
import { useReactMutation, useReactQuery } from "../../../services/queryService";
import { services } from "../../../services/services";
import { ConfirmationDialog } from "../../confirmationDialog/confirmationDialog";
import { useSnackbar } from "../../layout/snackbarProvider";
import { avatarStyles } from "../Dashboard/dashboard.styles";
import { OurServicesEditor } from "./OurServicesEditor";
import { alertStyles, blockedSaveHintStyles, dividerStyles, editingActionsStyles, emptyStateBoxStyles, emptyStateSubtitleStyles, eyebrowStyles, headerActionsStyles, loadingBoxStyles, pageHeaderContainerStyles, pageHeaderSubtitleStyles, pageHeaderTitleStyles, pageWrapperStyles, sectionBoxStyles, sectionTitleStyles, serviceCardStyles, serviceListItemStyles, serviceNameStyles, servicePriceStyles, tabsStyles } from "./ourServices.styles";

/**
 * The clinic's price list, and -- for the clinic's own administrator -- the place it is
 * written.
 *
 * The catalogue is per clinic and comes from the API, which takes the clinic from the
 * session; this page never names one. Edit mode is the same page rather than a separate
 * console screen, so an admin changes a price while looking at the page their customers
 * see. It is hidden from everyone else, and the API refuses the save regardless.
 *
 * The tab strip is a read-mode affordance only: editing shows every direction at once,
 * because a tab that hides half the unsaved changes behind it is a good way to lose them.
 */
export const OurServices = (): JSX.Element => {
    const theme = useTheme();
    const { i18n, t: translate } = useTranslation();
    const { isAuthenticated, role } = useAuth();
    const { hostContext, hostname } = useBranding();
    const { showSnackbar } = useSnackbar();

    /**
     * Signed in, this is the clinic's own price list. Signed out, it is the same price list
     * read through the public front-door route.
     *
     * This page has always been a PUBLIC route fetching an AUTHENTICATED endpoint, so a
     * visitor with no session got a 401 and a blank page. That was survivable while the only
     * address was the platform's; it is not now that a clinic's own domain is meant to be
     * their whole public presence.
     *
     * The public route names no clinic -- it resolves one from the hostname -- so there is
     * nothing to fetch when the hostname names none, which is what `enabled` says.
     */
    const { data, isLoading, refetch } = useReactQuery<IServiceCatalog>(
        isAuthenticated
            ? services.serviceCatalog.getServiceCatalog()
            : services.publicTenant.getServiceCatalog(hostname),
        { enabled: isAuthenticated || hostContext !== null }
    );

    const saveCatalog = useReactMutation(services.serviceCatalog.saveServiceCatalogMutation());

    const [activeDirection, setActiveDirection] = React.useState(0);
    /** The working copy while editing, or null when the page is read-only. */
    const [draft, setDraft] = React.useState<CatalogDraft | null>(null);
    const [isDiscardOpen, setIsDiscardOpen] = React.useState(false);

    /**
     * Whether the catalogue was actually fetched, which is not the same as "isError is
     * false". A query can also settle without data and without an error -- React Query
     * pauses one it believes cannot reach the network -- and treating that as an empty
     * result would tell a visitor the clinic offers nothing when nothing was ever loaded.
     */
    const hasLoaded: boolean = data !== undefined;
    const directions = data?.directions ?? [];
    const isEditing: boolean = draft !== null;
    const canEdit: boolean = role === 'ADMIN';

    // A direction can be removed while its tab is the selected one, and the read view is
    // redrawn from the saved catalogue as soon as editing ends.
    const selectedDirection: number = Math.min(activeDirection, Math.max(directions.length - 1, 0));

    const invalidKeys: Set<string> = React.useMemo(
        () => (draft ? invalidNodeKeys(draft) : new Set<string>()),
        [draft]
    );

    const startEditing = (): void => setDraft(toCatalogDraft(data));

    const cancelEditing = (): void => {
        setDraft(null);
        setIsDiscardOpen(false);
    };

    /**
     * Asks before discarding, because there is no autosave and a mistyped click here
     * loses everything typed since Edit. An untouched draft closes straight away --
     * confirming a decision the admin has not made yet is just noise.
     */
    const requestCancel = (): void => {
        const isUntouched: boolean =
            draft !== null
            && JSON.stringify(toServiceCatalogRequest(draft))
            === JSON.stringify(toServiceCatalogRequest(toCatalogDraft(data)));

        if (isUntouched) {
            cancelEditing();
            return;
        }

        setIsDiscardOpen(true);
    };

    const save = (): void => {
        if (!draft) {
            return;
        }

        saveCatalog.mutate(toServiceCatalogRequest(draft), {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.SERVICES_SAVE_SUCCESS), 'success');
                setDraft(null);
                setActiveDirection(0);
                refetch();
            },
            // The draft stays on screen: a failed save is the one moment the admin most
            // needs what they typed.
            onError: (error: unknown) => {
                const parsed = parseApiError(error);
                showSnackbar(parsed.message ?? translate(ResourcesIds.SERVICES_SAVE_ERROR), 'error');
            },
        });
    };

    const renderCatalog = (): JSX.Element => (
        <>
            {/* Directions Tabs */}
            <Tabs
                value={selectedDirection}
                onChange={(_, value) => setActiveDirection(value)}
                scrollButtons="auto"
                variant="fullWidth"
                sx={tabsStyles}
            >
                {directions.map((direction) => (
                    <Tab
                        key={direction.id}
                        label={localised(direction.titleRo, direction.titleEn, i18n.language)}
                    />
                ))}
            </Tabs>

            {/* Sections */}
            {directions[selectedDirection]?.sections.map((section) => (
                <Box key={section.id} sx={sectionBoxStyles}>
                    {/* Section Name */}
                    <Typography
                        variant="h5"
                        color="primary.dark"
                        sx={sectionTitleStyles}
                    >
                        {localised(section.titleRo, section.titleEn, i18n.language)}
                    </Typography>

                    <List disablePadding>
                        {section.types.map((type) => (
                            <Card key={type.id} sx={serviceCardStyles}>
                                {/* Service Name */}
                                <Typography
                                    variant="h6"
                                    color="text.primary"
                                    sx={serviceNameStyles}
                                >
                                    {localised(type.nameRo, type.nameEn, i18n.language)}
                                </Typography>

                                {/* Options */}
                                {type.options.map((option, idx) => (
                                    <React.Fragment key={option.id}>
                                        <ListItem sx={serviceListItemStyles}>
                                            <ListItemAvatar>
                                                <Avatar sx={avatarStyles(theme)}>
                                                    <HealthAndSafety />
                                                </Avatar>
                                            </ListItemAvatar>

                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" color="text.secondary">
                                                        {localised(option.durationRo, option.durationEn, i18n.language)}
                                                    </Typography>
                                                }
                                            />

                                            <Typography
                                                variant="body1"
                                                sx={servicePriceStyles(theme)}
                                            >
                                                {option.price.toFixed(2)} RON
                                            </Typography>
                                        </ListItem>

                                        {idx < type.options.length - 1 && <Divider sx={dividerStyles} />}
                                    </React.Fragment>
                                ))}
                            </Card>
                        ))}
                    </List>
                </Box>
            ))}
        </>
    );

    const renderEmptyState = (): JSX.Element => (
        <Box sx={emptyStateBoxStyles}>
            <Typography variant="h6" gutterBottom>
                {translate(ResourcesIds.NO_SERVICES_TITLE)}
            </Typography>
            <Typography variant="body2" sx={emptyStateSubtitleStyles}>
                {translate(canEdit
                    ? ResourcesIds.NO_SERVICES_ADMIN_SUBTITLE
                    : ResourcesIds.NO_SERVICES_SUBTITLE)}
            </Typography>
        </Box>
    );

    return (
        <Box sx={pageWrapperStyles}>
            {/* Page header */}
            <Box sx={pageHeaderContainerStyles}>
                <Box sx={eyebrowStyles}>{translate(ResourcesIds.SERVICES_TITLE)}</Box>
                <Typography variant="h3" component="h1" gutterBottom sx={pageHeaderTitleStyles}>
                    {translate(ResourcesIds.OUR_SERVICES_TITLE)}
                </Typography>
                <Typography variant="h6" sx={pageHeaderSubtitleStyles}>
                    {translate(ResourcesIds.OUR_SERVICES_SUBTITLE)}
                </Typography>
            </Box>

            {canEdit && hasLoaded && (
                <Box sx={isEditing ? editingActionsStyles : headerActionsStyles}>
                    {isEditing ? (
                        <>
                            <Button
                                variant="contained"
                                onClick={save}
                                disabled={invalidKeys.size > 0 || saveCatalog.isPending}
                            >
                                {translate(ResourcesIds.SERVICES_SAVE_ACTION)}
                            </Button>

                            <Button variant="outlined" onClick={requestCancel}>
                                {translate(ResourcesIds.CANCEL_TEXT)}
                            </Button>

                            {/* Travels with the button, so a disabled Save is never a
                                mystery from a part of the list that looks fine. */}
                            {invalidKeys.size > 0 && (
                                <Typography variant="body2" sx={blockedSaveHintStyles}>
                                    {/* `fields`, not `count`: i18next treats `count` as the
                                        plural selector, and Romanian's three plural forms
                                        are not worth carrying for a number badge. */}
                                    {translate(ResourcesIds.SERVICES_BLOCKED_SAVE, { fields: invalidKeys.size })}
                                </Typography>
                            )}
                        </>
                    ) : (
                        <Button variant="contained" startIcon={<EditIcon />} onClick={startEditing}>
                            {translate(ResourcesIds.SERVICES_EDIT_ACTION)}
                        </Button>
                    )}
                </Box>
            )}

            {!isLoading && !hasLoaded && (
                <Alert severity="error" sx={alertStyles}>
                    {translate(ResourcesIds.SERVICES_LOAD_ERROR)}
                </Alert>
            )}

            {isLoading ? (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress size={60} />
                </Box>
            ) : !hasLoaded ? null : draft !== null ? (
                <OurServicesEditor
                    draft={draft}
                    invalidKeys={invalidKeys}
                    onChange={setDraft}
                />
            ) : directions.length === 0 ? (
                renderEmptyState()
            ) : (
                renderCatalog()
            )}

            <ConfirmationDialog
                isOpen={isDiscardOpen}
                onClose={() => setIsDiscardOpen(false)}
                onConfirm={cancelEditing}
                title={translate(ResourcesIds.SERVICES_DISCARD_TITLE)}
                description={translate(ResourcesIds.SERVICES_DISCARD_DESCRIPTION)}
                confirmMessage={translate(ResourcesIds.SERVICES_DISCARD_CONFIRM)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
            />
        </Box>
    );
};
